import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import _ from 'lodash';
import { SetStateAction, StrictMode, useCallback, useEffect, useState } from 'react';
import { positions, Provider as AlertProvider, transitions } from 'react-alert';
import { BrowserRouter } from 'react-router-dom';
import './App.css';
import { UserContextProvider } from './contexts/UserContext';
import { DevTools } from './DevTools';
import Routes from './Routes';
import { getUser } from './services/users';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: true,
    },
  },
});

const alertOptions = {
  // you can also just use 'bottom center'
  position: positions.TOP_CENTER,
  timeout: 5000,
  offset: '30px',
  // you can also just use 'scale'
  transition: transitions.SCALE,
};

const AlertTemplate = ({ style, options, message, close }: { style: any; options: any; message: any; close: any }) => (
  <div style={style}>
    {options.type === 'info' && <div className="w-auto bg-cloud rounded-md p-5 text-base font-raleway">{message}</div>}
    {options.type === 'success' && <div className="w-auto bg-olive text-white rounded-md p-5 text-base font-raleway">{message}</div>}
    {options.type === 'error' && <div className="w-auto bg-ginger text-white rounded-md p-5 text-base font-raleway">{message}</div>}
  </div>
);

const App = () => {
  const [currentUser, setCurrentUser] = useState<SetStateAction<null | undefined | boolean> | boolean>(null);
  const [loggedIn, setLoggedIn] = useState<SetStateAction<null | undefined | boolean>>(null);
  const value = { currentUser, setCurrentUser };

  const setUser = useCallback(async () => {
    //get the currently logged in user
    const cognitoUser = await Auth.currentAuthenticatedUser().catch(async (err) => {
      setLoggedIn(false);
      setCurrentUser(false);
    });

    if (_.has(cognitoUser, 'attributes.sub')) {
      //now get the user information
      const user = await getUser(_.get(cognitoUser, 'attributes.sub'));
      setCurrentUser({ ...cognitoUser, user });
      setLoggedIn(true);
    } else {
      setLoggedIn(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await setUser();
    })();
  }, [setUser]);

  const stripeOptions = {
    // passing the client secret obtained from the server
    clientSecret: process.env.REACT_APP_STRIPE_SECRET_KEY,
  };

  return (
    <QueryClientProvider client={queryClient}>
      <UserContextProvider value={value}>
        <StrictMode>
          <BrowserRouter>
            <AlertProvider template={AlertTemplate} {...alertOptions}>
              <Routes loggedIn={loggedIn} />
            </AlertProvider>
          </BrowserRouter>
        </StrictMode>
      </UserContextProvider>
      <DevTools />
    </QueryClientProvider>
  );
};

export default App;
